import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AccountingFunctionsService,
  CurrentScopeFacade,
  UpdateHealthCardsService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  HealthPointOperationLabel,
  HealthPointVerificationDialogComponent,
  type IHealthPointVerificationDialogData,
} from './healthpoint-verification-dialog.component';
import { TyroService } from '@principle-theorem/ng-tyro';
import {
  Invoice,
  OrganisationCache,
  Practice,
} from '@principle-theorem/principle-core';
import {
  TransactionAction,
  TransactionProvider,
  type IHealthPointTransactionExtendedData,
  type IHealthcareClaim,
  type IInvoice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Region,
  WithRef,
  guardFilter,
  snapshot,
} from '@principle-theorem/shared';
import {
  isHealthPointTransactionCompleteCallbackData,
  isHealthPointTransactionErrorCallback,
  isHealthPointTransactionSuccessBaseCallbackData,
  isNotStarted,
  isTyroResponse,
  type IHealthPointTransactionErrorCallback,
} from '@principle-theorem/tyro';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  TransactionProviderType,
  type ITransactionProvider,
} from '../../transaction-provider';
import { TYRO_ERROR_DIALOG_DATA } from '../base-transaction';
import { SelectTyroTerminalService } from '../select-tyro-terminal/select-tyro-terminal.service';
import { HealthPointBuilder } from './health-point-builder.service';
import {
  toHealthPointCancelTransaction,
  toHealthPointPendingCancelTransaction,
  toHealthPointPendingTransaction,
  toHealthPointTransaction,
} from './health-point-transaction';

const TYRO_HEALTHPOINT_TIMEOUT_ERROR_CODE = '9100';

@Injectable()
export class TyroHealthPointTransactionProvider
  implements ITransactionProvider
{
  providerId = TransactionProvider.TyroHealthPoint;
  providerType = TransactionProviderType.HealthFund;
  providerRegions = [Region.Australia];
  isEnabled$: Observable<boolean>;

  constructor(
    private _tyro: TyroService,
    private _currentScopeFacade: CurrentScopeFacade,
    private _healthPointBuilder: HealthPointBuilder,
    private _selectTerminal: SelectTyroTerminalService,
    private _basicDialog: BasicDialogService,
    private _accountFunctions: AccountingFunctionsService,
    private _updateHealthCards: UpdateHealthCardsService,
    private _dialog: MatDialog
  ) {
    this.isEnabled$ = this._currentScopeFacade.currentPractice$.pipe(
      map((practice) => Practice.isTyroEnabled(practice))
    );
  }

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    const canClaim = Invoice.canAddTransactions(invoice);
    return of(canClaim);
  }

  async capture(
    invoice: WithRef<IInvoice>,
    claim: IHealthcareClaim
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const formData =
      await this._healthPointBuilder.openHealthPointForm(invoice);
    if (!formData) {
      return;
    }
    const terminal = await this._selectTerminal.getTerminalData(invoice, claim);
    if (!terminal) {
      return;
    }
    const request = await this._healthPointBuilder.buildHealthPointClaim(
      invoice,
      claim,
      formData,
      terminal.terminalData
    );
    const response = await snapshot(
      this._tyro
        .initiateHealthPointClaim$(request)
        .pipe(guardFilter(isTyroResponse))
    );
    if (isNotStarted(response) || !response) {
      return;
    }
    if (isHealthPointTransactionErrorCallback(response)) {
      return this._handleHealthPointErrorResponse(
        invoice,
        response,
        () =>
          toHealthPointPendingTransaction(
            invoice,
            terminal.practiceRef,
            claim,
            request,
            response
          ),
        TransactionAction.Add,
        claim,
        HealthPointOperationLabel.Claim
      );
    }
    if (!isHealthPointTransactionCompleteCallbackData(response)) {
      // eslint-disable-next-line no-console
      console.error('Invaid data for healthpoint response', response);
      await this._basicDialog.alert(TYRO_ERROR_DIALOG_DATA);
      return;
    }
    const transaction = await toHealthPointTransaction(
      invoice,
      terminal.practiceRef,
      claim,
      request,
      response
    );

    const result = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      transaction,
      TransactionAction.Add,
      claim
    );

    if (!result) {
      return;
    }
    const patient = await OrganisationCache.patients.getDoc(
      Invoice.patientRef(invoice)
    );
    await this._updateHealthCards.open(
      this._updateHealthCards.factory(patient).fromTransaction(transaction)
    );

    return result;
  }

  async cancel(
    invoice: WithRef<IInvoice>,
    transaction: WithRef<ITransaction<IHealthPointTransactionExtendedData>>,
    explicitClaim?: IHealthcareClaim
  ): Promise<void> {
    const claim =
      explicitClaim ??
      Invoice.findHealthcareClaimForTransaction(invoice, transaction);
    const terminal = await this._selectTerminal.getTerminalData(invoice, claim);
    if (!terminal) {
      return;
    }
    const request = this._healthPointBuilder.buildHealthPointClaimCancel(
      transaction,
      terminal.terminalData
    );
    if (!request) {
      return;
    }
    const response = await snapshot(
      this._tyro
        .cancelHealthPointClaim$(request)
        .pipe(guardFilter(isTyroResponse))
    );
    if (isNotStarted(response) || !response) {
      return;
    }
    if (isHealthPointTransactionErrorCallback(response)) {
      await this._handleHealthPointErrorResponse(
        invoice,
        response,
        () =>
          toHealthPointPendingCancelTransaction(
            invoice,
            terminal.practiceRef,
            claim,
            request,
            response,
            transaction
          ),
        TransactionAction.Cancel,
        claim,
        HealthPointOperationLabel.Cancellation
      );
      return;
    }
    if (!isHealthPointTransactionSuccessBaseCallbackData(response)) {
      // eslint-disable-next-line no-console
      console.error('Invalid data for healthpoint cancel response', response);
      await this._basicDialog.alert(TYRO_ERROR_DIALOG_DATA);
      return;
    }

    const cancelledTransaction = await toHealthPointCancelTransaction(
      invoice,
      terminal.practiceRef,
      claim,
      request,
      response,
      transaction
    );
    await this._accountFunctions.addTransactionToInvoice(
      invoice,
      cancelledTransaction,
      TransactionAction.Cancel,
      claim
    );
  }

  private async _handleHealthPointErrorResponse(
    invoice: WithRef<IInvoice>,
    response: IHealthPointTransactionErrorCallback,
    createTransactionFn: () => Promise<
      ITransaction<IHealthPointTransactionExtendedData>
    >,
    action: TransactionAction,
    claim: IHealthcareClaim | undefined,
    operationLabel: HealthPointOperationLabel
  ): Promise<DocumentReference<ITransaction> | undefined> {
    if (response.healthpointErrorCode === TYRO_HEALTHPOINT_TIMEOUT_ERROR_CODE) {
      const transaction = await createTransactionFn();
      const result = await this._accountFunctions.addTransactionToInvoice(
        invoice,
        transaction,
        action,
        claim
      );
      if (!result) {
        return;
      }

      await this._dialog
        .open<
          HealthPointVerificationDialogComponent,
          IHealthPointVerificationDialogData
        >(
          HealthPointVerificationDialogComponent,
          DialogPresets.medium<IHealthPointVerificationDialogData>({
            data: {
              operationLabel,
              errorCode: response.healthpointErrorCode,
              errorDescription: response.healthpointErrorDescription,
              refTag: response.healthpointRefTag,
            },
          })
        )
        .afterClosed()
        .toPromise();
      return result;
    }
    await this._basicDialog.alert({
      ...TYRO_ERROR_DIALOG_DATA,
      title: 'Notice',
      prompt: [
        `Error Code: ${response.healthpointErrorCode}`,
        `Error Description: ${response.healthpointErrorDescription}`,
        `Ref Tag: ${response.healthpointRefTag}`,
      ],
    });
  }
}
